/**
 * Attendance module service.
 *
 * Covers the 22 Attendance pages in svdy-platform-web. Requires the
 * organization to hold the `qutime` license (license middleware gates
 * this at /attendance/*).
 *
 * Grouping mirrors the frontend Attendance page tree:
 *   rules → shifts (breaks/timetables/cycles) → schedules → leave →
 *   geofences → time-entries → approvals → calculation → reports.
 *
 * Types: where backend contracts exist in schema.d.ts we use them
 * directly (Schema<'BreakResponse'> etc). All services now ship with
 * typed wrappers — no `unknown`-typed stubs remain.
 */

import { http } from './client';
import type { Schema } from './types';

// Typed response / payload shapes
export type Break = Schema<'BreakResponse'>;
export type BreakCreate = Schema<'BreakCreate'>;
export type BreakUpdate = Schema<'BreakUpdate'>;

export type Timetable = Schema<'TimetableResponse'>;
export type TimetableCreate = Schema<'TimetableCreate'>;
export type TimetableUpdate = Schema<'TimetableUpdate'>;

export type ShiftCycle = Schema<'ShiftCycleResponse'>;
export type ShiftCycleCreate = Schema<'ShiftCycleCreate'>;
export type ShiftCycleUpdate = Schema<'ShiftCycleUpdate'>;

export type AttendanceRule = Schema<'AttendanceRuleResponse'>;
export type AttendanceRuleUpdate = Schema<'AttendanceRuleUpdate'>;

export type LeaveType = Schema<'LeaveTypeResponse'>;
export type LeaveTypeCreate = Schema<'LeaveTypeCreate'>;
export type LeaveTypeUpdate = Schema<'LeaveTypeUpdate'>;

export type LeavePolicy = Schema<'LeavePolicyResponse'>;
export type LeavePolicyCreate = Schema<'LeavePolicyCreate'>;
export type LeavePolicyUpdate = Schema<'LeavePolicyUpdate'>;

export type LeaveBalance = Schema<'LeaveBalanceResponse'>;

export type PublicHoliday = Schema<'PublicHolidayResponse'>;
export type PublicHolidayCreate = Schema<'PublicHolidayCreate'>;
export type PublicHolidayUpdate = Schema<'PublicHolidayUpdate'>;
export type PublicHolidayImportCountry = Schema<'PublicHolidayImportCountry'>;
export type PublicHolidayImportSubdivision = Schema<'PublicHolidayImportSubdivision'>;
export type PublicHolidayImportOptions = Schema<'PublicHolidayImportOptionsResponse'>;
export type PublicHolidaySuggestion = Schema<'PublicHolidaySuggestion'>;
export type PublicHolidaySuggestions = Schema<'PublicHolidaySuggestionsResponse'>;
export type PublicHolidayImportRequest = Schema<'PublicHolidayImportRequest'>;
export type PublicHolidayImportResult = Schema<'PublicHolidayImportResult'>;

export type Geofence = Schema<'GeofenceResponse'>;
export type GeofenceCreate = Schema<'GeofenceCreate'>;
export type GeofenceUpdate = Schema<'GeofenceUpdate'>;

// ADR-032 Kiosk — shared-device admin management.
export type KioskDevice = Schema<'KioskDeviceResponse'>;
export type KioskDeviceCreate = Schema<'KioskDeviceCreate'>;
export type KioskDeviceUpdate = Schema<'KioskDeviceUpdate'>;
export type KioskDeviceRegister = Schema<'KioskDeviceRegisterResponse'>;
export type KioskSettings = Schema<'KioskSettingsResponse'>;
export type KioskSettingsUpdate = Schema<'KioskSettingsUpdate'>;

export type TimeEntry = Schema<'TimeEntryResponse'>;
export type TimePunch = Schema<'TimePunchResponse'>;
export type OrgPunch = Schema<'OrgPunchResponse'>;
export type GeoInfo = Schema<'GeoInfo'>;
export type ManualEntryCreate = Schema<'ManualEntryCreate'>;

export type Schedule = Schema<'ScheduleResponse'>;
export type DeptScheduleAssign = Schema<'DeptScheduleAssign'>;
export type WorkerScheduleAssign = Schema<'WorkerScheduleAssign'>;
export type TempScheduleCreate = Schema<'TempScheduleCreate'>;
export type TempScheduleUpdate = Schema<'TempScheduleUpdate'>;
export type ScheduleViewRow = Schema<'ScheduleViewRow'>;
/** One cell of the schedule grid. Typed on the backend since 2026-08-12 — it was an
 *  untyped dict, so the UI's hand-written interface could drift from what is sent. */
export type ScheduleViewDay = Schema<'ScheduleViewDay'>;

export type CalculationRequest = Schema<'CalculationRequest'>;
export type CalculationStatus = Schema<'CalculationStatusResponse'>;
export type CalculationResult = Schema<'CalculationResultResponse'>;

export type ReportDefinition = Schema<'ReportDefinition'>;
export type ReportPreview = Schema<'ReportPreviewResponse'>;

export type Approval = Schema<'ApprovalResponse'>;
export type ApprovalCreate = Schema<'ApprovalCreate'>;
export type ApprovalUpdate = Schema<'ApprovalUpdate'>;
export type BulkApprovalUpdate = Schema<'BulkApprovalUpdate'>;
export type BulkDelete = Schema<'BulkDelete'>;
export type WorkerRef = Schema<'WorkerRef'>;

/** Canonical `type` values the backend stores on `approval_requests.type`. */
export type ApprovalType =
  | 'leave'
  | 'overtime'
  | 'missed_clock_in'
  | 'business_trip'
  | 'off_site_work'
  | 'schedule_change'
  | 'face_enrollment'
  | 'clock_in_face_review';

/** Canonical `status` lifecycle values. */
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ApprovalListQuery {
  type?: ApprovalType;
  status?: ApprovalStatus;
  start_date?: string;
  end_date?: string;
}

// ---------------------------------------------------------------------------
// Overview (dashboard KPI cards)
// ---------------------------------------------------------------------------

/**
 * The org-today KPI block returned by GET /v1/attendance/overview.
 *
 * Field names are pinned by the backend ASGI smoke
 * (tests/attendance/test_overview_kpis_smoke.py). Note `present_today`
 * (NOT `present`) — the AttendanceOverview page historically read the
 * wrong key and silently rendered 0. The shared OrgAttendanceKpis
 * component is now the single reader of this shape.
 */
export interface AttendanceOverviewKpis {
  present_today: number;
  absent: number;
  late: number;
  early_leave: number;
  on_leave: number;
  pending_approvals: number;
  anomalies: number;
}

export interface AttendanceOverviewResponse {
  kpis: AttendanceOverviewKpis;
  pending_approvals: {
    id: string;
    worker: { id: string; name: string };
    type: string;
    detail: string;
    age: number;
    priority: string;
  }[];
  anomalies: {
    id: string;
    worker: { id: string; name: string };
    issue: string;
    time: string | null;
    department: string | null;
  }[];
  department_summary: {
    department: string;
    present: number;
    absent: number;
    late: number;
    on_leave: number;
    total: number;
  }[];
  approval_summary: { type: string; count: number }[];
}

export const overviewApi = {
  get: () => http.get<AttendanceOverviewResponse>('/v1/attendance/overview'),
};

// ---------------------------------------------------------------------------
// Rules — global + per-department overrides
// ---------------------------------------------------------------------------

export const rulesApi = {
  /** Organization-wide rules. Always returns one row (auto-created on
   *  first fetch if absent). */
  getGlobal: () => http.get<AttendanceRule>('/v1/attendance/rules/global'),
  updateGlobal: (data: AttendanceRuleUpdate) =>
    http.put<AttendanceRule>('/v1/attendance/rules/global', data),
  /** Departments that have an override row (sparse; most departments
   *  inherit from global). */
  listDepartmentRules: () =>
    http.get<AttendanceRule[]>('/v1/attendance/rules/departments'),
  setDepartmentRules: (departmentId: string, data: AttendanceRuleUpdate) =>
    http.put<AttendanceRule>(
      `/v1/attendance/rules/departments/${departmentId}`,
      data,
    ),
  deleteDepartmentRules: (departmentId: string) =>
    http.delete(`/v1/attendance/rules/departments/${departmentId}`),
  /** ADR-016 — Worker-group scoped overrides (crosses departments). */
  listGroupRules: () =>
    http.get<AttendanceRule[]>('/v1/attendance/rules/groups'),
  setGroupRules: (groupId: string, data: AttendanceRuleUpdate) =>
    http.put<AttendanceRule>(`/v1/attendance/rules/groups/${groupId}`, data),
  deleteGroupRules: (groupId: string) =>
    http.delete(`/v1/attendance/rules/groups/${groupId}`),
};

// ---------------------------------------------------------------------------
// Shifts: breaks, timetables, shift cycles
// ---------------------------------------------------------------------------

export const breaksApi = {
  list: () => http.get<Break[]>('/v1/attendance/breaks'),
  create: (data: BreakCreate) =>
    http.post<Break>('/v1/attendance/breaks', data),
  update: (id: string, data: BreakUpdate) =>
    http.patch<Break>(`/v1/attendance/breaks/${id}`, data),
  delete: (id: string) => http.delete(`/v1/attendance/breaks/${id}`),
};

export const timetablesApi = {
  list: () => http.get<Timetable[]>('/v1/attendance/timetables'),
  create: (data: TimetableCreate) =>
    http.post<Timetable>('/v1/attendance/timetables', data),
  update: (id: string, data: TimetableUpdate) =>
    http.patch<Timetable>(`/v1/attendance/timetables/${id}`, data),
  delete: (id: string) => http.delete(`/v1/attendance/timetables/${id}`),
};

export const shiftCyclesApi = {
  list: () => http.get<ShiftCycle[]>('/v1/attendance/shift-cycles'),
  create: (data: ShiftCycleCreate) =>
    http.post<ShiftCycle>('/v1/attendance/shift-cycles', data),
  update: (id: string, data: ShiftCycleUpdate) =>
    http.patch<ShiftCycle>(`/v1/attendance/shift-cycles/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/shift-cycles/${id}`),
};

// ---------------------------------------------------------------------------
// Schedules — 4 distinct pages share this surface.
// ---------------------------------------------------------------------------

export interface ScheduleViewQuery {
  start_date: string;
  end_date: string;
  department_id?: string;
  location_id?: string;
}

function scheduleViewQS(q: ScheduleViewQuery): string {
  const parts = [
    `start_date=${encodeURIComponent(q.start_date)}`,
    `end_date=${encodeURIComponent(q.end_date)}`,
  ];
  if (q.department_id)
    parts.push(`department_id=${encodeURIComponent(q.department_id)}`);
  if (q.location_id)
    parts.push(`location_id=${encodeURIComponent(q.location_id)}`);
  return parts.join('&');
}

export const schedulesApi = {
  /** Calendar grid data for ScheduleView (workers × days). */
  getView: (query: ScheduleViewQuery) =>
    http.get<ScheduleViewRow[]>(
      `/v1/attendance/schedules/view?${scheduleViewQS(query)}`,
    ),
  listDepartmentSchedules: () =>
    http.get<Schedule[]>('/v1/attendance/schedules/departments'),
  assignDepartmentSchedule: (
    departmentId: string,
    data: DeptScheduleAssign,
  ) =>
    http.put<Schedule>(
      `/v1/attendance/schedules/departments/${departmentId}`,
      data,
    ),
  listWorkerSchedules: () =>
    http.get<Schedule[]>('/v1/attendance/schedules/workers'),
  assignWorkerSchedule: (workerId: string, data: WorkerScheduleAssign) =>
    http.put<Schedule>(
      `/v1/attendance/schedules/workers/${workerId}`,
      data,
    ),
  removeWorkerSchedule: (scheduleId: string) =>
    http.delete(`/v1/attendance/schedules/workers/${scheduleId}`),
  listTemporarySchedules: () =>
    http.get<Schedule[]>('/v1/attendance/schedules/temporary'),
  createTemporarySchedule: (data: TempScheduleCreate) =>
    http.post<Schedule>('/v1/attendance/schedules/temporary', data),
  updateTemporarySchedule: (scheduleId: string, data: TempScheduleUpdate) =>
    http.patch<Schedule>(
      `/v1/attendance/schedules/temporary/${scheduleId}`,
      data,
    ),
  deleteTemporarySchedule: (scheduleId: string) =>
    http.delete(`/v1/attendance/schedules/temporary/${scheduleId}`),
};

// ---------------------------------------------------------------------------
// Leave: types, policies, public holidays, balances
// ---------------------------------------------------------------------------

export const leaveTypesApi = {
  list: () => http.get<LeaveType[]>('/v1/attendance/leave-types'),
  create: (data: LeaveTypeCreate) =>
    http.post<LeaveType>('/v1/attendance/leave-types', data),
  update: (id: string, data: LeaveTypeUpdate) =>
    http.patch<LeaveType>(`/v1/attendance/leave-types/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/leave-types/${id}`),
};

export const leavePoliciesApi = {
  list: () => http.get<LeavePolicy[]>('/v1/attendance/leave-policies'),
  create: (data: LeavePolicyCreate) =>
    http.post<LeavePolicy>('/v1/attendance/leave-policies', data),
  update: (id: string, data: LeavePolicyUpdate) =>
    http.patch<LeavePolicy>(`/v1/attendance/leave-policies/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/leave-policies/${id}`),
};

export interface PublicHolidaySuggestionsQuery {
  location_id: string;
  country: string;
  year: number;
  subdivision?: string;
  language: string;
}

function holidayQuery(params: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  const value = query.toString();
  return value ? `?${value}` : '';
}

export const publicHolidaysApi = {
  list: (locationId?: string, includeLegacy = false) =>
    http.get<PublicHoliday[]>(
      `/v1/attendance/public-holidays${holidayQuery({
        location_id: locationId,
        include_legacy: includeLegacy || undefined,
      })}`,
    ),
  create: (data: PublicHolidayCreate) =>
    http.post<PublicHoliday>('/v1/attendance/public-holidays', data),
  update: (id: string, data: PublicHolidayUpdate) =>
    http.patch<PublicHoliday>(`/v1/attendance/public-holidays/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/public-holidays/${id}`),
  importOptions: (language: string, country?: string) =>
    http.get<PublicHolidayImportOptions>(
      `/v1/attendance/public-holidays/import-options${holidayQuery({ language, country })}`,
    ),
  suggestions: (query: PublicHolidaySuggestionsQuery) =>
    http.get<PublicHolidaySuggestions>(
      `/v1/attendance/public-holidays/suggestions${holidayQuery({
        location_id: query.location_id,
        country: query.country,
        year: query.year,
        subdivision: query.subdivision,
        language: query.language,
      })}`,
    ),
  import: (data: PublicHolidayImportRequest) =>
    http.post<PublicHolidayImportResult>('/v1/attendance/public-holidays/import', data),
};

export const leaveBalancesApi = {
  list: (departmentId?: string) =>
    http.get<LeaveBalance[]>(
      `/v1/attendance/leave-balances${
        departmentId ? `?department_id=${departmentId}` : ''
      }`,
    ),
  adjust: (id: string, data: { amount: number; reason?: string }) =>
    http.post<LeaveBalance>(`/v1/attendance/leave-balances/${id}/adjust`, data),
  /** Org-level health summary — counts + coverage % + suggested
   *  next actions. Drives the LeavePolicies admin page banner so
   *  admins can self-diagnose 'why is /me/leave empty'. */
  diagnose: () =>
    http.get<LeaveDiagnoseResponse>('/v1/attendance/leave-balances/diagnose'),
};

export interface LeaveDiagnoseResponse {
  active_workers: number;
  invited_workers: number;
  leave_types: number;
  leave_policies: number;
  leave_balance_rows: number;
  expected_balance_rows: number;
  coverage_pct: number;
  suggested_actions: string[];
}

// ---------------------------------------------------------------------------
// Geofences
// ---------------------------------------------------------------------------

export type GeofenceScope = 'department' | 'worker' | 'group' | 'global';

export const geofencesApi = {
  list: (scope?: GeofenceScope) =>
    http.get<Geofence[]>(
      `/v1/attendance/geofences${scope ? `?scope=${scope}` : ''}`,
    ),
  create: (data: GeofenceCreate) =>
    http.post<Geofence>('/v1/attendance/geofences', data),
  update: (id: string, data: GeofenceUpdate) =>
    http.patch<Geofence>(`/v1/attendance/geofences/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/geofences/${id}`),
};

// ---------------------------------------------------------------------------
// Kiosk (ADR-032) — shared-device admin management. Device-facing endpoints
// (/v1/kiosk/*) use device-token auth and live on the device, not here.
// ---------------------------------------------------------------------------

export const kioskApi = {
  listDevices: () =>
    http.get<KioskDevice[]>('/v1/attendance/kiosk/devices'),
  registerDevice: (data: KioskDeviceCreate) =>
    http.post<KioskDeviceRegister>('/v1/attendance/kiosk/devices', data),
  updateDevice: (id: string, data: KioskDeviceUpdate) =>
    http.patch<KioskDevice>(`/v1/attendance/kiosk/devices/${id}`, data),
  regeneratePairingCode: (id: string) =>
    http.post<KioskDeviceRegister>(`/v1/attendance/kiosk/devices/${id}/pairing-code`, {}),
  revokeDevice: (id: string) =>
    http.delete(`/v1/attendance/kiosk/devices/${id}`),
  deleteDevice: (id: string) =>
    http.delete(`/v1/attendance/kiosk/devices/${id}/permanent`),
  reindexFaces: () =>
    http.post<{ indexed: number; missing: number }>(`/v1/attendance/kiosk/reindex-faces`, {}),
  getSettings: () =>
    http.get<KioskSettings>('/v1/attendance/kiosk/settings'),
  updateSettings: (data: KioskSettingsUpdate) =>
    http.patch<KioskSettings>('/v1/attendance/kiosk/settings', data),
};

// ---------------------------------------------------------------------------
// Time entries
// ---------------------------------------------------------------------------

export interface TimeEntriesQuery {
  worker_id?: string;
  start_date?: string;
  end_date?: string;
  department_id?: string;
  location_id?: string;
  status?: string;
  /** Free-text search over worker name or code. */
  worker?: string;
}

function timeEntriesQS(q?: TimeEntriesQuery): string {
  if (!q) return '';
  const parts: string[] = [];
  if (q.worker_id) parts.push(`worker_id=${encodeURIComponent(q.worker_id)}`);
  if (q.start_date) parts.push(`start_date=${encodeURIComponent(q.start_date)}`);
  if (q.end_date) parts.push(`end_date=${encodeURIComponent(q.end_date)}`);
  if (q.department_id) parts.push(`department_id=${encodeURIComponent(q.department_id)}`);
  if (q.location_id) parts.push(`location_id=${encodeURIComponent(q.location_id)}`);
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (q.worker) parts.push(`worker=${encodeURIComponent(q.worker)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const timeEntriesApi = {
  list: (query?: TimeEntriesQuery) =>
    http.get<TimeEntry[]>(`/v1/attendance/time-entries${timeEntriesQS(query)}`),
  get: (id: string) =>
    http.get<TimeEntry>(`/v1/attendance/time-entries/${id}`),
  /** ADR-028 — every punch of this entry's worker-day (valid/invalid +
   *  reason; calc picks earliest valid = clock-in, latest valid = clock-out). */
  punches: (id: string) =>
    http.get<TimePunch[]>(`/v1/attendance/time-entries/${id}/punches`),
  /** ADR-028 — org-wide punch stream (live monitor / Clock Events): EVERY
   *  punch in the range, newest first, with worker identity. A worker who
   *  punches 5 times yields 5 rows. */
  orgPunches: (params: { start_date: string; end_date: string; worker_id?: string; limit?: number }) => {
    const p: string[] = [
      `start_date=${encodeURIComponent(params.start_date)}`,
      `end_date=${encodeURIComponent(params.end_date)}`,
    ];
    if (params.worker_id) p.push(`worker_id=${encodeURIComponent(params.worker_id)}`);
    if (params.limit != null) p.push(`limit=${params.limit}`);
    return http.get<OrgPunch[]>(`/v1/attendance/punches?${p.join('&')}`);
  },
  /** Admin-side correction: add an after-the-fact clock-in/out pair for
   *  an worker. Backs "补打卡" / "Add Manual Entry" flows. */
  manual: (data: ManualEntryCreate) =>
    http.post<TimeEntry>('/v1/attendance/time-entries/manual', data),
  clockIn: (data?: { geo?: { lat: number; lng: number; address?: string } }) =>
    http.post<TimeEntry>('/v1/attendance/time-entries/clock-in', data ?? {}),
  clockOut: (data?: { geo?: { lat: number; lng: number; address?: string } }) =>
    http.post<TimeEntry>('/v1/attendance/time-entries/clock-out', data ?? {}),
};

// ---------------------------------------------------------------------------
// Approvals — 6 approval pages share this endpoint with a filter
// ---------------------------------------------------------------------------

function approvalsQuery(query?: ApprovalListQuery): string {
  if (!query) return '';
  const parts: string[] = [];
  if (query.type) parts.push(`type=${encodeURIComponent(query.type)}`);
  if (query.status) parts.push(`status=${encodeURIComponent(query.status)}`);
  if (query.start_date)
    parts.push(`start_date=${encodeURIComponent(query.start_date)}`);
  if (query.end_date)
    parts.push(`end_date=${encodeURIComponent(query.end_date)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const approvalsApi = {
  /** Business-day count for a NAMED worker, for the record-on-behalf dialog.
   *  `/v1/me/business-days` cannot serve it — that one counts for the
   *  authenticated worker, and rest days plus holidays are resolved per worker.
   *  Preview only: `create_approval` recomputes on submit and ignores whatever
   *  the client sends. */
  businessDays: (workerId: string, start: string, end: string) =>
    http.get<{ total_days: number; business_days: number }>(
      `/v1/attendance/business-days?worker_id=${encodeURIComponent(workerId)}`
      + `&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    ),
  /** The worker's CURRENT shift on a date, for the record-on-behalf dialog.
   *
   *  Same response shape as the worker-facing `/me/shifts`, and the same server-side
   *  implementation — two shapes for one question is how the day counts drifted apart.
   *  `current_shift: null` means unscheduled that day, which is a real answer. */
  workerShift: (workerId: string, on: string) =>
    http.get<{
      date: string;
      current_shift: { id: string; name: string; clock_in: string | null; clock_out: string | null } | null;
      available_shifts: { id: string; name: string; clock_in: string | null; clock_out: string | null }[];
    }>(
      `/v1/attendance/worker-shift?worker_id=${encodeURIComponent(workerId)}`
      + `&date=${encodeURIComponent(on)}`,
    ),
  list: (query?: ApprovalListQuery) =>
    http.get<Approval[]>(`/v1/attendance/approvals${approvalsQuery(query)}`),
  create: (data: ApprovalCreate) =>
    http.post<Approval>('/v1/attendance/approvals', data),
  update: (id: string, data: ApprovalUpdate) =>
    http.patch<Approval>(`/v1/attendance/approvals/${id}`, data),
  bulkUpdate: (data: BulkApprovalUpdate) =>
    http.patch<Approval[]>('/v1/attendance/approvals/bulk', data),
  delete: (id: string) =>
    http.delete(`/v1/attendance/approvals/${id}`),
  bulkDelete: (data: BulkDelete) =>
    http.delete('/v1/attendance/approvals/bulk', { data }),
};

// ---------------------------------------------------------------------------
// Calculation engine (Phase 3 stub — pages already wired for when real
// implementation lands; backend returns empty `results` for now).
// ---------------------------------------------------------------------------

export const calculationApi = {
  run: (data: CalculationRequest) =>
    http.post<CalculationStatus>('/v1/attendance/calculation/jobs', data),
  status: (jobId: string) =>
    http.get<CalculationStatus>(`/v1/attendance/calculation/status/${jobId}`),
  results: () =>
    http.get<CalculationResult>('/v1/attendance/calculation/results'),
};

// ---------------------------------------------------------------------------
// Reports (Phase 3 stub)
// ---------------------------------------------------------------------------

export const reportsApi = {
  list: () => http.get<ReportDefinition[]>('/v1/attendance/reports'),
  generate: (reportId: string) =>
    http.post<CalculationStatus>(
      `/v1/attendance/reports/${reportId}/generate`,
    ),
  preview: (reportId: string, filters?: { start_date?: string; end_date?: string; department_id?: string }) => {
    const qs = filters
      ? '?' + new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : '';
    return http.get<ReportPreview>(`/v1/attendance/reports/${reportId}/preview${qs}`);
  },
  // `export` removed 2026-08-09 along with its two backend endpoints. It returned the
  // same JSON as `preview`, nothing ever called it, and thirty days of production logs
  // recorded zero requests to any report path. The reports page writes the file
  // client-side from the preview it already holds — with Excel and PDF and localised
  // headers, none of which the server-side CSV had.
};
